import { DataGrid, useNotifications } from '@ijoin/components';
import {
  GatedQueuePriorityRow,
  useGetAdminGatedQueuePriorities,
  usePatchAdminGatedQueuePriority
} from '@ijoin/hooks';
import { Button, Slider, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { useEffect, useState } from 'react';

const WEIGHT_MIN = 1;
const WEIGHT_MAX = 10;
const WEIGHT_MARKS = Array.from({ length: WEIGHT_MAX }, (_, i) => ({
  label: String(i + 1),
  value: i + 1
}));

const QueuesSettingsPage = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const navigate = useNavigate();
  const priorities = useGetAdminGatedQueuePriorities();
  const updatePriority = usePatchAdminGatedQueuePriority();

  const [draft, setDraft] = useState<Record<string, number>>({});

  useEffect(() => {
    const data = priorities.data?.data;

    if (!data) {
      return;
    }

    setDraft(Object.fromEntries(data.map(row => [row.queueName, row.weight])));
  }, [priorities.data]);

  const rows = priorities.data?.data ?? [];
  const dirtyRows = rows.filter(row => draft[row.queueName] !== row.weight);
  const isDirty = dirtyRows.length > 0;

  const onSave = async () => {
    const results = await Promise.allSettled(
      dirtyRows.map(row =>
        updatePriority.mutateAsync({
          data: { weight: draft[row.queueName] },
          queueName: row.queueName
        })
      )
    );

    const failed = results.filter(r => r.status === 'rejected').length;

    queryClient.invalidateQueries({
      queryKey: ['/api/v3/admin/gated-queue-priorities']
    });

    if (failed) {
      notifications.error(
        `Failed to update ${failed} of ${dirtyRows.length} queue priorities`
      );
      return;
    }

    notifications.success(
      `Updated ${dirtyRows.length} queue priorit${dirtyRows.length === 1 ? 'y' : 'ies'}`
    );
  };

  const onReset = () => {
    setDraft(Object.fromEntries(rows.map(row => [row.queueName, row.weight])));
  };

  const columns: GridColDef<GatedQueuePriorityRow>[] = [
    {
      field: 'queueName',
      flex: 1,
      headerName: 'Queue',
      renderCell: params => (
        <Button
          onClick={() =>
            navigate({
              params: { name: params.row.queueName },
              to: '/queues/$name'
            })
          }
          sx={{ textTransform: 'none' }}
          variant='text'>
          {params.row.queueName}
        </Button>
      )
    },
    {
      field: 'weight',
      flex: 2,
      headerName: 'Weight',
      renderCell: params => (
        <Stack direction='row' sx={{ height: '100%', px: 2, width: '100%' }}>
          <Slider
            data-testid={`weight-${params.row.queueName}`}
            marks={WEIGHT_MARKS}
            max={WEIGHT_MAX}
            min={WEIGHT_MIN}
            onChange={(_, value) =>
              setDraft(prev => ({
                ...prev,
                [params.row.queueName]: value as number
              }))
            }
            step={1}
            value={draft[params.row.queueName] ?? params.row.weight}
            valueLabelDisplay='auto'
          />
        </Stack>
      ),
      sortable: false
    }
  ];

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
      <Stack alignItems='center' direction='row' justifyContent='space-between'>
        <Typography variant='h6'>Gated Queue Priorities</Typography>
        <Stack direction='row' spacing={1}>
          <Button
            data-testid='resetQueuePriorities'
            disabled={!isDirty || updatePriority.isPending}
            onClick={onReset}
            variant='outlined'>
            Reset
          </Button>
          <Button
            data-testid='saveQueuePriorities'
            disabled={!isDirty || updatePriority.isPending}
            onClick={onSave}
            variant='contained'>
            Save Changes
          </Button>
        </Stack>
      </Stack>
      <Typography color='text.secondary' variant='body2'>
        Higher weights receive a larger share of each integration&apos;s
        in-flight slots when gated queues compete. Defaults to 1 across the
        board (equal share).
      </Typography>

      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={row => row.queueName}
        hideFooterPagination
        loading={priorities.isFetching}
        rowHeight={64}
        rows={rows}
        showColumnMenu
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'queuePriorities'
          }
        }}
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Stack>
  );
};

export const Route = createFileRoute('/queues/settings')({
  component: QueuesSettingsPage
});
